'use client';

import { type CSSProperties, type ReactNode } from 'react';
import { MetaDefault } from '../../../../../components/typography';
import type { CardRenderContext } from '../../tool-config-types';
import { FlatToolCard } from '../shared/flat-tool-card';
import { getHITLArtifactHeaderActions } from '../shared/hitl-artifact-header-actions';
import {
  dispatchPortalToolNavigate,
  getPortalToolDisplayConfig,
} from './portal-tool-display-config';
import {
  BrowserAutomationScreenshotPreview,
  readBrowserAutomationScreenshotBase64,
} from './browser-automation-screenshot-preview';

type PortalToolCardBodyProps = {
  actionLabel: string;
  cardDetail?: string;
  resultSummary?: string;
  screenshotBase64?: string;
  isRunning: boolean;
  durationMs?: number;
  navigationLabel?: string;
  onNavigate?: () => void;
  headerActions?: ReactNode;
};

const runningLabelShimmerStyle: CSSProperties = {
  background:
    'linear-gradient(90deg, var(--colors-fg-muted) 25%, var(--colors-fg-subtle) 50%, var(--colors-fg-muted) 75%)',
  backgroundSize: '200% 100%',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  animationTimingFunction: 'linear',
};

function PortalToolCardBody({
  actionLabel,
  cardDetail,
  resultSummary,
  screenshotBase64,
  isRunning,
  durationMs,
  navigationLabel,
  onNavigate,
  headerActions,
}: PortalToolCardBodyProps) {
  return (
    <FlatToolCard
      title={actionLabel}
      isRunning={isRunning}
      durationMs={durationMs}
      headerActions={headerActions}
    >
      {cardDetail && <MetaDefault color="fg.muted">{cardDetail}</MetaDefault>}
      {resultSummary && (
        <MetaDefault
          color="fg.muted"
          whiteSpace="pre-wrap"
          animationStyle={isRunning ? 'shimmer' : undefined}
          style={isRunning ? runningLabelShimmerStyle : undefined}
        >
          {resultSummary}
        </MetaDefault>
      )}
      {screenshotBase64 && !isRunning && (
        <BrowserAutomationScreenshotPreview
          screenshotBase64={screenshotBase64}
        />
      )}
      {navigationLabel && onNavigate && (
        <MetaDefault
          as="button"
          color="colorPalette.default"
          cursor="pointer"
          onClick={onNavigate}
          alignSelf="flex-start"
        >
          {navigationLabel}
        </MetaDefault>
      )}
    </FlatToolCard>
  );
}

export function renderPortalToolCardForToolName({
  ctx,
  toolName,
}: {
  ctx: CardRenderContext;
  toolName: string;
}): ReactNode {
  const displayConfig = getPortalToolDisplayConfig(toolName);

  if (!displayConfig) {
    return null;
  }

  const isRunning =
    ctx.toolCallData.status === 'started' ||
    ctx.toolCallData.status === 'in-progress';
  const actionLabel = displayConfig.formatActionLabel(ctx.toolCallData.input);
  const cardDetail = displayConfig.formatCardDetail?.({
    input: ctx.toolCallData.input,
    output: ctx.toolCallData.output,
  });
  const resultSummary = isRunning
    ? displayConfig.runningText
    : displayConfig.formatResultSummary(ctx.toolCallData.output);
  const navigation = displayConfig.getNavigation(ctx.toolCallData.output);
  const screenshotBase64 = readBrowserAutomationScreenshotBase64(
    ctx.toolCallData.output,
  );

  return (
    <PortalToolCardBody
      actionLabel={actionLabel}
      cardDetail={cardDetail}
      resultSummary={resultSummary}
      screenshotBase64={screenshotBase64}
      isRunning={isRunning}
      durationMs={ctx.toolCallData.timing?.durationMs}
      headerActions={getHITLArtifactHeaderActions(ctx)}
      navigationLabel={navigation?.label}
      onNavigate={
        navigation
          ? () => {
              dispatchPortalToolNavigate(navigation);
            }
          : undefined
      }
    />
  );
}

export function createPortalToolCardRenderer(toolName: string) {
  return (ctx: CardRenderContext) =>
    renderPortalToolCardForToolName({ ctx, toolName });
}
